import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { getThemeById } from '../config/themes'
import type { TenantThemeConfig } from '../types/theme'
import * as authService from '../services/authService'
import * as walletService from '../services/walletService'
import { isInGameSession, getGameSession } from '../services/casinoService'
import {
  getDefaultWithdrawalSettings,
  getWithdrawalSettings,
  type WithdrawalSettings,
} from '../services/withdrawalSettingsService'
import { createWithdrawal } from '../services/walletTransactionService'
import './ModernWithdrawPage.css'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernWithdrawPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

const translateWithdrawalErrorMessage = (errorMessage: string): string => {
  const normalizedMessage = String(errorMessage || '').trim()
  if (!normalizedMessage) {
    return 'เกิดข้อผิดพลาดในการส่งคำขอถอนเงิน'
  }

  const minimumMatch = normalizedMessage.match(/^Minimum withdrawal amount is\s+(\d+(?:\.\d+)?)$/i)
  if (minimumMatch) {
    return `ยอดถอนขั้นต่ำคือ ${walletService.formatCurrency(Number(minimumMatch[1]))} บาท`
  }

  const maximumMatch = normalizedMessage.match(/^Maximum withdrawal amount per transaction is\s+(\d+(?:\.\d+)?)$/i)
  if (maximumMatch) {
    return `ยอดถอนสูงสุดต่อครั้งคือ ${walletService.formatCurrency(Number(maximumMatch[1]))} บาท`
  }

  const dailyLimitMatch = normalizedMessage.match(/^Daily withdrawal request limit exceeded\s*\((\d+)\s+per day\)$/i)
  if (dailyLimitMatch) {
    return `คุณทำรายการถอนครบจำนวน ${dailyLimitMatch[1]} ครั้งต่อวันแล้ว`
  }

  const dailyAmountLimitMatch = normalizedMessage.match(/^Daily withdrawal amount limit exceeded\s*\((\d+(?:\.\d+)?)\s+per day\)$/i)
  if (dailyAmountLimitMatch) {
    return `ยอดถอนรวมต่อวันเกิน ${walletService.formatCurrency(Number(dailyAmountLimitMatch[1]))} บาทแล้ว`
  }

  if (/^HTTP(?:\s+error!?\s*status:)?\s*\d+/i.test(normalizedMessage) || /^Failed to fetch$/i.test(normalizedMessage)) {
    return 'ไม่สามารถเชื่อมต่อระบบถอนได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง'
  }

  const mappedMessages: Record<string, string> = {
    'Validation failed': 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบแล้วลองใหม่อีกครั้ง',
    'Tenant ID and User ID are required': 'ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่อีกครั้ง',
    'Invalid amount': 'จำนวนเงินถอนไม่ถูกต้อง',
    'Wallet not found': 'ไม่พบบัญชีกระเป๋าเงิน กรุณาติดต่อแอดมิน',
    'Withdrawal blocked because bonus turnover or bonus conditions are not complete': 'ยังมีเงื่อนไขโบนัสหรือ turnover ที่ต้องทำให้ครบก่อนถอนเงิน',
    'Withdrawal blocked because cashback turnover is not complete': 'ยังมีเงื่อนไข cashback turnover ที่ต้องทำให้ครบก่อนถอนเงิน',
    'Withdrawal requires bonus forfeiture before it can continue': 'รายการถอนนี้ต้องสละสิทธิ์โบนัสก่อนจึงจะดำเนินการต่อได้',
    'Insufficient wallet balance': 'ยอดเงินในกระเป๋าไม่เพียงพอ',
    'Invalid withdrawal amount': 'จำนวนเงินถอนไม่ถูกต้อง',
    'Unable to load withdrawal settings': 'ไม่สามารถโหลดเงื่อนไขการถอนได้ กรุณาลองใหม่อีกครั้ง',
  }

  return mappedMessages[normalizedMessage] || normalizedMessage
}

const getWithdrawalApprovalStatusText = (
  amountNumber: number,
  withdrawalSettings: WithdrawalSettings
): string => {
  const autoApproveDisabled = withdrawalSettings.autoWithdrawalConditions.autoApproveEnabled === false
  const maxAutoApprovalAmount = Number(withdrawalSettings.autoWithdrawalConditions.maxAutoApprovalAmount || 0)
  const exceedsAutoApprovalLimit =
    maxAutoApprovalAmount >= 0 && amountNumber > maxAutoApprovalAmount

  if (autoApproveDisabled) {
    return 'ระบบปิดการอนุมัติอัตโนมัติ และรายการนี้จะรอเจ้าหน้าที่อนุมัติ'
  }

  if (exceedsAutoApprovalLimit) {
    return `ยอดนี้เกิน ${walletService.formatCurrency(maxAutoApprovalAmount)} บาท และจะรอเจ้าหน้าที่อนุมัติ`
  }

  return 'อยู่ในเกณฑ์ดำเนินการอัตโนมัติ'
}

/** URL โลโก้ธนาคารจาก bankCode หรือ imageUrl */
function getBankLogoUrl(account: authService.UserBankAccount | null, getAssetUrl: (p: string) => string): string {
  if (!account) return getAssetUrl('/img/b/KBANK.png')
  if (account.imageUrl) return account.imageUrl
  const code = (account.bankCode || '').toUpperCase()
  return getAssetUrl(`/img/b/${code || 'KBANK'}.png`)
}

/** หน้าถอนเงิน — แปลงจาก withdraw.html: หัวข้อ, บัญชีธนาคารของผู้ใช้, ยอดคงเหลือ, ช่องจำนวน, ปุ่มยืนยัน */
const ModernWithdrawPage: React.FC<ModernWithdrawPageProps> = ({ themeConfig, assetConfig }) => {
  const navigate = useNavigate()
  const [amount, setAmount] = useState('')
  const [walletData, setWalletData] = useState<walletService.WalletData | null>(null)
  const [userBankAccounts, setUserBankAccounts] = useState<authService.UserBankAccount[]>([])
  const [bankCheckDone, setBankCheckDone] = useState(false)
  const [isLoadingSettings, setIsLoadingSettings] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [withdrawalSettings, setWithdrawalSettings] = useState<WithdrawalSettings>(getDefaultWithdrawalSettings())

  // ── Game session guard: ป้องกันถอนขณะเล่นเกม ──────────────────────────────
  const [inGameSession, setInGameSession] = useState(() => isInGameSession())

  // Re-check on localStorage changes (e.g. game exits in the same tab or another tab)
  useEffect(() => {
    const check = () => setInGameSession(isInGameSession())
    window.addEventListener('storage', check)
    // Poll every 2 s as safety net (same-tab changes don't fire 'storage')
    const interval = setInterval(check, 2000)
    return () => {
      window.removeEventListener('storage', check)
      clearInterval(interval)
    }
  }, [])

  const themeFolder = useMemo(() => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue', [themeConfig.themeId])
  const getAssetUrl = (path: string) => {
    const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${themeFolder}` : `/${themeFolder}`
    return base + (path.startsWith('/') ? path : `/${path}`)
  }

  const primaryBank = userBankAccounts[0] ?? null
  const bankLogoUrl = getBankLogoUrl(primaryBank, getAssetUrl)
  const availableBalance = walletData?.balance?.main || 0

  useEffect(() => {
    if (!authService.isAuthenticated()) return
    walletService.getWallet().then((walletRes) => {
      if (walletRes.success && walletRes.data) setWalletData(walletRes.data)
      else setWalletData(null)
    })
  }, [])

  useEffect(() => {
    let active = true

    const loadSettings = async () => {
      setIsLoadingSettings(true)
      const nextSettings = await getWithdrawalSettings()
      if (active) {
        setWithdrawalSettings(nextSettings)
        setIsLoadingSettings(false)
      }
    }

    loadSettings()

    const handleSettingsUpdate = () => {
      loadSettings()
    }

    window.addEventListener('deposit_channels_updated', handleSettingsUpdate)
    return () => {
      active = false
      window.removeEventListener('deposit_channels_updated', handleSettingsUpdate)
    }
  }, [])

  // ดึงบัญชีธนาคารของผู้ใช้; ถ้ายังไม่มีให้ไปกรอกที่ /member
  useEffect(() => {
    if (!authService.isAuthenticated()) {
      setBankCheckDone(true)
      return
    }
    authService.fetchUserBankAccounts().then((list) => {
      setBankCheckDone(true)
      setUserBankAccounts(list ?? [])
      if (!list || list.length === 0) navigate('/member', { replace: true })
    })
  }, [navigate])

  const balanceText = walletData != null
    ? walletService.formatCurrency(availableBalance) + ' ฿'
    : '0.00 ฿'

  const withdrawEnabled = themeConfig.settings?.withdrawEnabled !== false

  if (!bankCheckDone) {
    return (
      <div className="content htd-withdraw">
        <div className="htd-withdraw__glow" aria-hidden />
        <div className="htd-withdraw__loading">กำลังโหลด...</div>
      </div>
    )
  }

  // ── Block withdrawal while player is in a game session ──────────────────────
  if (inGameSession) {
    const session = getGameSession()
    return (
      <div className="content htd-withdraw">
        <div className="htd-withdraw__glow" aria-hidden />
        <div className="htd-withdraw__blocked htd-withdraw__blocked--game">
          <span className="htd-withdraw__blocked-icon" aria-hidden>
            <i className="fas fa-gamepad" />
          </span>
          <h2>ไม่สามารถถอนเงินขณะเล่นเกมได้</h2>
          <p>
            {session
              ? `คุณกำลังเล่น "${session.gameName}" — กรุณาออกจากเกมก่อน แล้วกลับมาถอนเงิน`
              : 'กรุณาออกจากเกมก่อน แล้วกลับมาถอนเงิน'}
          </p>
          <button
            type="button"
            className="htd-withdraw__blocked-btn"
            onClick={() => {
              window.location.hash = '/lobby-casino'
            }}
          >
            กลับไปหน้าเกม
          </button>
        </div>
      </div>
    )
  }

  if (!withdrawEnabled) {
    return (
      <div className="content htd-withdraw">
        <div className="htd-withdraw__glow" aria-hidden />
        <div className="htd-withdraw__blocked">
          <span className="htd-withdraw__blocked-icon" aria-hidden>
            <i className="fas fa-ban" />
          </span>
          <h2>ปิดการถอนเงินชั่วคราว</h2>
          <p>ขณะนี้ระบบปิดการถอนเงิน กรุณาติดต่อแอดมินหรือลองใหม่ในภายหลัง</p>
        </div>
      </div>
    )
  }

  const accountName = primaryBank?.accountName ?? '–'
  const accountNumber = primaryBank?.accountNumber ?? '–'
  const bankName = primaryBank?.bankName ?? '–'

  const checkWithdrawalConditions = () => {
    const currentTime = new Date()
    const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes()
    const [shutdownHour, shutdownMinute] = withdrawalSettings.withdrawalBot.shutdownTime.split(':').map(Number)
    const [activationHour, activationMinute] = withdrawalSettings.withdrawalBot.activationTime.split(':').map(Number)
    const shutdownMinutes = shutdownHour * 60 + shutdownMinute
    const activationMinutes = activationHour * 60 + activationMinute

    let isBotActive = true
    if (shutdownMinutes > activationMinutes) {
      isBotActive = currentMinutes >= activationMinutes && currentMinutes < shutdownMinutes
    } else {
      isBotActive = currentMinutes >= activationMinutes || currentMinutes < shutdownMinutes
    }

    if (!isBotActive) {
      return {
        isValid: false,
        message: `ระบบถอนเงินปิดชั่วคราว เปิดให้บริการเวลา ${withdrawalSettings.withdrawalBot.shutdownTime} - ${withdrawalSettings.withdrawalBot.activationTime}`,
      }
    }

    return { isValid: true }
  }

  const handleSubmitWithdrawal = async () => {
    const amountNumber = Number(amount)

    if (!amount || !Number.isFinite(amountNumber) || amountNumber <= 0) {
      await Swal.fire({ icon: 'warning', title: 'กรุณาระบุจำนวนเงินที่ต้องการถอน', confirmButtonColor: 'var(--htd-primary)' })
      return
    }

    if (isLoadingSettings) {
      await Swal.fire({ icon: 'info', title: 'กำลังโหลดการตั้งค่า', text: 'กรุณารอสักครู่แล้วลองใหม่อีกครั้ง', confirmButtonColor: 'var(--htd-primary)' })
      return
    }

    if (!primaryBank) {
      await Swal.fire({ icon: 'warning', title: 'ยังไม่พบบัญชีธนาคาร', text: 'กรุณาเพิ่มบัญชีธนาคารก่อนทำรายการถอนเงิน', confirmButtonColor: 'var(--htd-primary)' })
      navigate('/member')
      return
    }

    if (amountNumber < withdrawalSettings.withdrawalConditions.minWithdrawalAmount) {
      await Swal.fire({
        icon: 'warning',
        title: 'จำนวนเงินไม่ถูกต้อง',
        text: `ถอนเงินขั้นต่ำ ${walletService.formatCurrency(withdrawalSettings.withdrawalConditions.minWithdrawalAmount)} บาท`,
        confirmButtonColor: 'var(--htd-primary)',
      })
      return
    }

    if (
      withdrawalSettings.autoWithdrawalConditions.maxWithdrawalPerTransaction > 0 &&
      amountNumber > withdrawalSettings.autoWithdrawalConditions.maxWithdrawalPerTransaction
    ) {
      await Swal.fire({
        icon: 'warning',
        title: 'ยอดถอนเกินกำหนด',
        text: `ถอนได้ไม่เกิน ${walletService.formatCurrency(withdrawalSettings.autoWithdrawalConditions.maxWithdrawalPerTransaction)} บาท ต่อครั้ง`,
        confirmButtonColor: 'var(--htd-primary)',
      })
      return
    }

    if (amountNumber > availableBalance) {
      await Swal.fire({
        icon: 'error',
        title: 'ยอดเงินไม่เพียงพอ',
        text: `ยอดเงินคงเหลือ ${walletService.formatCurrency(availableBalance)} บาท`,
        confirmButtonColor: 'var(--htd-primary)',
      })
      return
    }

    const conditionCheck = checkWithdrawalConditions()
    if (!conditionCheck.isValid) {
      await Swal.fire({
        icon: 'error',
        title: 'ไม่สามารถถอนเงินได้',
        text: conditionCheck.message,
        confirmButtonColor: 'var(--htd-primary)',
      })
      return
    }

    const requiresManualApproval =
      withdrawalSettings.autoWithdrawalConditions.autoApproveEnabled === false ||
      (
        withdrawalSettings.autoWithdrawalConditions.maxAutoApprovalAmount >= 0 &&
        amountNumber > withdrawalSettings.autoWithdrawalConditions.maxAutoApprovalAmount
      )

    const confirmed = await Swal.fire({
      icon: 'question',
      title: 'ยืนยันการถอนเงิน',
      html: `
        <div style="text-align:left">
          <p><strong>จำนวนเงิน:</strong> ${walletService.formatCurrency(amountNumber)} บาท</p>
          <p><strong>ธนาคาร:</strong> ${bankName}</p>
          <p><strong>เลขบัญชี:</strong> ${accountNumber}</p>
          <p><strong>ชื่อบัญชี:</strong> ${accountName}</p>
          <p><strong>สถานะ:</strong> ${getWithdrawalApprovalStatusText(amountNumber, withdrawalSettings)}</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'ยืนยันถอนเงิน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: 'var(--htd-primary)',
      cancelButtonColor: '#64748b',
    })

    if (!confirmed.isConfirmed) {
      return
    }

    setIsSubmitting(true)
    try {
      const response = await createWithdrawal({
        amount: amountNumber,
        bankAccount: {
          bankCode: primaryBank.bankCode,
          accountNumber: primaryBank.accountNumber,
          accountName: primaryBank.accountName,
        },
        reason: 'user_withdrawal_request',
      })

      if (!response.success) {
        if (response.data?.bonuses?.length) {
          const bonusItems = response.data.bonuses
            .map((bonus) => {
              const remaining = Number(bonus.wageringRemaining || 0)
              const amount = Number(bonus.amount || 0)
              const note = String(bonus.note || '').trim()
              return `<li style="margin-bottom:8px;"><strong>${bonus.type || 'โบนัส'}</strong> วงเงิน ${walletService.formatCurrency(amount)} บาท, turnover คงเหลือ ${walletService.formatCurrency(remaining)} บาท${note ? `<br/><span style="color:#6b7280;">${note}</span>` : ''}</li>`
            })
            .join('')

          await Swal.fire({
            icon: 'warning',
            title: 'ยังถอนเงินไม่ได้',
            html: `
              <div style="text-align:left">
                <p>${translateWithdrawalErrorMessage(response.message || response.error || 'ยังมีเงื่อนไขโบนัสหรือ turnover ที่ต้องทำให้ครบก่อนถอนเงิน')}</p>
                <p style="margin-top:12px;"><strong>turnover คงเหลือรวม:</strong> ${walletService.formatCurrency(Number(response.data.totalRemainingWagering || 0))} บาท</p>
                <ul style="padding-left:18px;margin-top:12px;">${bonusItems}</ul>
              </div>
            `,
            confirmButtonColor: 'var(--htd-primary)',
          })
          return
        }

        if (response.code === 'WITHDRAW_REQUEST_COOLDOWN_ACTIVE') {
          throw new Error(
            response.data?.userMessage
            || 'คุณเพิ่งส่งคำขอถอนเงินไปแล้ว กรุณารอสักครู่แล้วลองใหม่อีกครั้ง'
          )
        }

        throw new Error(response.message || response.error || 'ไม่สามารถส่งคำขอถอนเงินได้')
      }

      const refreshedWallet = await walletService.getWallet()
      if (refreshedWallet.success && refreshedWallet.data) {
        setWalletData(refreshedWallet.data)
      }
      setAmount('')

      await Swal.fire({
        icon: 'success',
        title: 'ส่งคำขอถอนเงินสำเร็จ',
        text: requiresManualApproval
          ? `คำขอถอนเงิน ${walletService.formatCurrency(amountNumber)} บาท ถูกส่งแล้วและกำลังรอเจ้าหน้าที่อนุมัติ`
          : `คำขอถอนเงิน ${walletService.formatCurrency(amountNumber)} บาท ถูกส่งเรียบร้อยแล้ว`,
        confirmButtonColor: 'var(--htd-primary)',
      })

      navigate('/history/withdraw')
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'ถอนเงินไม่สำเร็จ',
        text: translateWithdrawalErrorMessage(error instanceof Error ? error.message : ''),
        confirmButtonColor: 'var(--htd-primary)',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="content htd-withdraw">
      <div className="htd-withdraw__glow" aria-hidden />
      <div className="htd-withdraw__inner">
        <header className="htd-withdraw__hero htd-withdraw-anim htd-withdraw-anim--1">
          <div className="htd-withdraw__badge" aria-hidden>
            <i className="fas fa-money-bill-wave" />
          </div>
          <div>
            <p className="htd-withdraw__eyebrow">Withdraw</p>
            <h1 className="htd-withdraw__title">ถอนเงิน</h1>
            <p className="htd-withdraw__desc">โอนเข้าบัญชีธนาคารที่ผูกไว้ — ระบุยอดแล้วยืนยัน</p>
          </div>
        </header>

        <div className="htd-withdraw__actions htd-withdraw-anim htd-withdraw-anim--2">
          <Link to="/topup" className="htd-withdraw__action htd-withdraw__action--deposit">
            <i className="fas fa-arrow-down" aria-hidden />
            ฝากเงิน
          </Link>
          <span className="htd-withdraw__action htd-withdraw__action--withdraw">
            <i className="fas fa-arrow-up" aria-hidden />
            ถอนเงิน
          </span>
        </div>

        <section className="htd-withdraw__balance htd-withdraw-anim htd-withdraw-anim--2" aria-label="ยอดเครดิต">
          <div>
            <span className="htd-withdraw__balance-label">ยอดเครดิตที่ใช้ถอนได้</span>
            <span className="htd-withdraw__balance-value">{balanceText}</span>
          </div>
          <Link to="/history/withdraw" className="htd-withdraw__balance-link">
            ประวัติถอนเงิน
          </Link>
        </section>

        <section className="htd-withdraw__bank htd-withdraw-anim htd-withdraw-anim--3" aria-label="บัญชีปลายทาง">
          <p className="htd-withdraw__panel-label">โอนเข้าบัญชีนี้</p>
          <div className="htd-withdraw__bank-row">
            <img
              src={bankLogoUrl}
              alt={bankName}
              className="htd-withdraw__bank-logo"
              onError={(e) => {
                ;(e.target as HTMLImageElement).src = getAssetUrl('/img/b/KBANK.png')
              }}
            />
            <div className="htd-withdraw__bank-meta">
              <h2 className="htd-withdraw__bank-name">{bankName}</h2>
              <p className="htd-withdraw__bank-line">
                <span>ชื่อบัญชี</span>
                {accountName}
              </p>
              <p className="htd-withdraw__bank-line">
                <span>เลขบัญชี</span>
                {accountNumber}
              </p>
            </div>
          </div>
          <Link to="/member" className="htd-withdraw__bank-edit">
            จัดการบัญชีธนาคาร
          </Link>
        </section>

        <section className="htd-withdraw__amount htd-withdraw-anim htd-withdraw-anim--4" aria-label="จำนวนเงินถอน">
          <p className="htd-withdraw__panel-label">จำนวนเงินที่ต้องการถอน</p>
          <input
            type="text"
            inputMode="numeric"
            placeholder="กรอกจำนวนเงิน"
            className="htd-withdraw__input"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))}
            aria-label="จำนวนเงินที่ต้องการถอน"
          />
          <div className="htd-withdraw__hints">
            <p className="htd-withdraw__hint">
              {isLoadingSettings
                ? 'กำลังโหลดเงื่อนไขการถอน...'
                : `ถอนขั้นต่ำ ${walletService.formatCurrency(withdrawalSettings.withdrawalConditions.minWithdrawalAmount)} บาท • สูงสุด ${walletService.formatCurrency(withdrawalSettings.autoWithdrawalConditions.maxWithdrawalPerTransaction)} บาท / ครั้ง`}
            </p>
            {!isLoadingSettings ? (
              <p className="htd-withdraw__hint htd-withdraw__hint--muted">
                เวลาปิดบริการถอน: {withdrawalSettings.withdrawalBot.shutdownTime} -{' '}
                {withdrawalSettings.withdrawalBot.activationTime}
              </p>
            ) : null}
          </div>
        </section>

        <button
          type="button"
          className="htd-withdraw__submit htd-withdraw-anim htd-withdraw-anim--5"
          onClick={handleSubmitWithdrawal}
          disabled={isSubmitting || isLoadingSettings || !primaryBank}
        >
          {isSubmitting ? 'กำลังส่งคำขอ...' : 'ยืนยันถอนเงินเข้าบัญชี'}
        </button>
      </div>
    </div>
  )
}

export default ModernWithdrawPage
